import classNames from 'classnames';

import IconCheck from '~images/inline/checkmark-decorative.inline.svg';
import IconX from '~images/inline/x-decorative.inline.svg';

type ComparisonItem = {
    desktopName: string;
    mobileName: string;
};

type FeatureValues = {
    icon: 'cross' | 'check' | 'cross_check' | 'none';
    disclaimer?: string;
};

type Props = {
    itemstocompare: string;
    features: string;
    values: string;
};

const ComparisonTable = ({ itemstocompare, features, values }: Props) => {
    const headings = itemstocompare ? (JSON.parse(itemstocompare) as ComparisonItem[]) : [];
    const rows = features ? (JSON.parse(features) as string[]) : [];
    const featureValues = values ? (JSON.parse(values) as FeatureValues[][]) : [[]];

    const getRowClasses = (rowIndex: number) =>
        classNames('outline outline-2 outline-gray-light outline-offset-0', {
            'bg-white': rowIndex % 2 === 0,
            'bg-[#F0F0F0] bg-opacity-30': rowIndex % 2 !== 0,
            'rounded-t-lg': rowIndex === 0,
            'rounded-b-lg': rowIndex === rows.length - 1,
        });

    return (
        <table className="w-full border-separate border-spacing-y-0.5">
            <thead>
                <tr>
                    <th />
                    {/* Item or product in the comparison */}
                    {headings.map(({ desktopName, mobileName }, i) => (
                        <th className="text-center lg:p-6" key={`item-${i}`}>
                            <span
                                className="hidden lg:block w-56"
                                dangerouslySetInnerHTML={{ __html: desktopName }}
                            />
                            <span
                                className="lg:hidden"
                                dangerouslySetInnerHTML={{ __html: mobileName }}
                            />
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((feature, rowIndex) => (
                    <tr key={`feature-${rowIndex}`} className={getRowClasses(rowIndex)}>
                        {/* Feature name */}
                        <td className="px-3 py-5 lg:px-6 lg:py-6 text-left">
                            <span dangerouslySetInnerHTML={{ __html: feature }} />
                        </td>
                        {featureValues[rowIndex].map(({ icon, disclaimer }, colIndex) => (
                            <td
                                key={`value-${rowIndex}-${colIndex}`}
                                className="border-l-2 border-gray-light p-1 lg:p-4 min-w-10"
                            >
                                {/* Feature value for each item/product */}
                                <div className="flex flex-col items-center justify-center">
                                    {icon === 'cross_check' && (
                                        <span className="flex gap-2">
                                            <IconX className="text-icon-alert text-24 lg:text-48" />
                                            <IconCheck className="text-icon-primary text-24 lg:text-48" />
                                        </span>
                                    )}
                                    {icon === 'cross' && (
                                        <IconX className="text-icon-alert text-24 lg:text-48" />
                                    )}
                                    {icon === 'check' && (
                                        <IconCheck className="text-icon-primary text-24 lg:text-48" />
                                    )}
                                    {disclaimer && (
                                        <div className="text-green text-center text-14 font-bold lg:font-normal">
                                            {disclaimer}
                                        </div>
                                    )}
                                </div>
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default ComparisonTable;
